import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { pagePanel, pageTableHead, pageTableRow } from '../layout/page-styles'

export type TableColumn<T> = {
  header: React.ReactNode
  cell?: (row: T, index: number) => React.ReactNode
  accessor?: keyof T | ((row: T) => React.ReactNode)
  className?: string
}

export type TableProps<T> = {
  columns: TableColumn<T>[]
  data: T[] | undefined | null
  rowKey: (row: T, index: number) => string
  emptyMessage?: string
  className?: string
  dense?: boolean
  embedded?: boolean
  /** Makes the whole row navigate to this href when clicked / activated. */
  getRowHref?: (row: T, index: number) => string | undefined
}

export function Table<T>({
  columns,
  data,
  rowKey,
  emptyMessage,
  className,
  dense,
  embedded,
  getRowHref,
}: TableProps<T>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const resolvedEmptyMessage = emptyMessage ?? t('common.noDataFound')
  const hasRows = !!data && data.length > 0
  const cellPad = dense ? 'px-4 py-2' : 'px-4 py-3'

  const table = (
    <div className={cn(!embedded && 'overflow-x-auto', embedded && className)}>
      <table className="w-full">
          <thead>
            <tr className={pageTableHead}>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={cn(
                    cellPad,
                    'text-left text-xs font-semibold text-neutral-700',
                    c.className
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hasRows &&
              data!.map((row, rowIndex) => {
                const href = getRowHref?.(row, rowIndex)
                return (
                  <tr
                    key={rowKey(row, rowIndex)}
                    className={cn(pageTableRow, href && 'cursor-pointer')}
                    onClick={href ? () => navigate(href) : undefined}
                    onKeyDown={
                      href
                        ? e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              navigate(href)
                            }
                          }
                        : undefined
                    }
                    tabIndex={href ? 0 : undefined}
                    role={href ? 'link' : undefined}
                  >
                    {columns.map((c, colIndex) => {
                      let content: React.ReactNode
                      if (c.cell) content = c.cell(row, rowIndex)
                      else if (typeof c.accessor === 'function') content = c.accessor(row)
                      else if (typeof c.accessor === 'string') content = (row as any)[c.accessor]
                      else content = null
                      return (
                        <td
                          key={colIndex}
                          className={cn(cellPad, 'text-sm text-neutral-700', c.className)}
                        >
                          {content}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            {!hasRows && (
              <tr>
                <td colSpan={columns.length} className={cn(cellPad, 'text-center text-sm text-neutral-500 py-12')}>
                  {resolvedEmptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
  )

  if (embedded) return table

  return <div className={cn(pagePanel, className)}>{table}</div>
}

export default Table
